import { NetworkStatus, QueryResult } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'

/**
 * Call Apollo query's `refetch` method when dependencies (e.g. token) change.
 *
 * Hook supports `useQuery()` and `useLazyQuery()` - include `called` when using
 * lazy queries.
 */
export const useRefetchOnChange = <TData = unknown, TVariables = Record<string, unknown>>(
  {
    called,
    networkStatus,
    refetch,
  }: Pick<QueryResult<TData, TVariables>, 'networkStatus' | 'refetch'> & { called?: boolean },
  deps: ReadonlyArray<unknown>,
): void => {
  const [refetchRequired, setRefetchRequired] = useState(false)
  const calledRef = useRef<boolean | undefined>(called)
  const enabledRef = useRef(false)

  useEffect(() => {
    calledRef.current = called
  }, [called])

  useEffect(() => {
    // skip initial render
    if (!enabledRef.current) {
      enabledRef.current = true
      return
    }
    // `called` is only populated by useLazyQuery
    if (calledRef.current !== false) {
      setRefetchRequired(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  // avoid executing `refetch()` while query in-flight
  useEffect(() => {
    if (networkStatus === NetworkStatus.ready && refetchRequired) {
      setRefetchRequired(false)
      refetch()
    }
  }, [networkStatus, refetch, refetchRequired])
}
